import React, { useState } from 'react';
import {
    ScrollView,
    StyleSheet,
    Text,
    View,
} from 'react-native';
import { Stack, usePathname, useRouter } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import Header from '../Header';
import Footer from '../Footer';
import MetaTags from '../MetaTags';
import Button from '../ui/Button';
import AppRouteBootScreen from '../auth/AppRouteBootScreen';
import ProjectCreationUpgradeBanner from '../billing/ProjectCreationUpgradeBanner';
import ChatModal from '../discovery/Chat';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { fonts, interRegular, interSemiBold } from '../../lib/fonts';
import { useForwardedOnboardingState } from '../../lib/hooks/useForwardedOnboardingState';
import { buildCanonicalDashboardPath, buildDashboardPath } from '../../lib/dashboardRoutes';
import { createProjectFromChatDraft, type ChatDigestDraft } from '../../lib/chatProjectCreation';
import { useProjectCreationGate } from '../../lib/hooks/useProjectCreationGate';
import {
    ForwardedOnboardingStepper,
    ImportedPaperPreview,
} from './ForwardedOnboardingShared';
import ForwardedSystemMap from './ForwardedSystemMap';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';

export default function ForwardedSourceConnectedScreen() {
    const router = useRouter();
    const pathname = usePathname();
    const { fontsLoaded } = useFontLoading();
    const onboardingState = useForwardedOnboardingState();
    const [isChatVisible, setIsChatVisible] = useState(false);
    const projectCreationGate = useProjectCreationGate();

    const handleChatComplete = async (data: ChatDigestDraft, context?: { chatSessionId: string | null }) => {
        const result = await createProjectFromChatDraft(data, {
            source: 'forward',
            chatSessionId: context?.chatSessionId ?? null,
        });
        router.replace(result.destination);
    };

    const handleOpenChat = () => {
        projectCreationGate.guardProjectCreation(() => setIsChatVisible(true));
    };

    if (onboardingState.isLoading) {
        return <AppRouteBootScreen message={msg({
            id: 'forwardedSource.loadingImports',
            message: 'Loading your forwarded imports...',
        })} />;
    }

    if (!onboardingState.isEligible) {
        router.replace(buildDashboardPath(pathname));
        return <AppRouteBootScreen message={msg({ id: 'common.redirecting', message: 'Redirecting...' })} />;
    }

    return (
        <>
            <MetaTags
                title="Source Connected - Paperzilla"
                description="Set up your research topic from your forwarded alerts."
                url={`https://paperzilla.ai${buildCanonicalDashboardPath('/onboarding/source-connected')}`}
            />
            <Stack.Screen options={{ headerShown: false }} />
            <View style={styles.page}>
                <Header />
                <ScrollView
                    style={styles.scrollView}
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator={false}
                >
                    <View style={styles.contentWrapper}>
                        <View style={styles.content}>
                            <View style={styles.hero}>
                                <ForwardedOnboardingStepper />
                                <View style={styles.copyBlock}>
                                    <Text
                                        style={[styles.title, fontsLoaded && interSemiBold]}
                                        testID="forwarded-source-title"
                                    ><Trans id="onboarding.forwardedsourceconnectedscreen.yourFirstSourceIsConnected" comment="UI copy in components/onboarding/ForwardedSourceConnectedScreen.tsx">
                                        Your first source is connected
                                    </Trans></Text>
                                    <View style={styles.bodyList}>
                                        <View style={styles.bodyItem}>
                                            <View style={styles.bodyBullet} />
                                            <Text style={[styles.bodyText, fontsLoaded && interRegular]}><Trans id="onboarding.forwardedsourceconnectedscreen.paperzillaCombinesYourAlertsWithOtherSourcesFiltersThemTo" comment="UI copy in components/onboarding/ForwardedSourceConnectedScreen.tsx">
                                                Paperzilla combines your alerts with other sources, filters them to your research topic, and delivers one focused feed everywhere.
                                            </Trans></Text>
                                        </View>
                                        <View style={styles.bodyItem}>
                                            <View style={styles.bodyBullet} />
                                            <Text style={[styles.bodyText, fontsLoaded && interRegular]}><Trans id="onboarding.forwardedsourceconnectedscreen.weImportedPapersFromYourForwardedAlertNowSetUp" comment="UI copy in components/onboarding/ForwardedSourceConnectedScreen.tsx">
                                                We imported papers from your forwarded alert; now set up your topic so Paperzilla can add them to it while keeping the original emails visible in Inbox.
                                            </Trans></Text>
                                        </View>
                                    </View>
                                </View>
                                <ForwardedSystemMap />
                                <ImportedPaperPreview
                                    previewItems={onboardingState.previewItems}
                                    fallbackMessage={onboardingState.fallbackMessage}
                                    importedPaperCount={onboardingState.importedPaperCount}
                                />
                                <ProjectCreationUpgradeBanner
                                    visible={projectCreationGate.blockedNoticeVisible}
                                    onDismiss={projectCreationGate.dismissBlockedNotice}
                                    style={styles.projectCreationBanner}
                                    snapshot={projectCreationGate.snapshot}
                                />
                                <View style={styles.actionRow}>
                                    <Button
                                        label={msg({ id: 'forwardedSource.setUpTopic', message: 'Set up topic' })}
                                        onPress={handleOpenChat}
                                        testID="forwarded-source-setup-topic"
                                    />
                                </View>
                            </View>
                        </View>
                    </View>
                    <Footer />
                </ScrollView>
                <StatusBar style="auto" />
            </View>

            <ChatModal
                visible={isChatVisible}
                onClose={() => setIsChatVisible(false)}
                onComplete={handleChatComplete}
                entrySurface="forwarded_source_connected"
                variant="forwarded"
                forwardedContext={{
                    importCount: onboardingState.importCount,
                    importedPaperCount: onboardingState.importedPaperCount,
                }}
            />
        </>
    );
}

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: colors.bgWeak,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        flexGrow: 1,
        paddingBottom: 0,
    },
    contentWrapper: {
        flex: 1,
    },
    content: {
        alignSelf: 'center',
        maxWidth: 1100,
        paddingHorizontal: 20,
        paddingVertical: 24,
        width: '100%',
    },
    hero: {
        gap: 22,
    },
    projectCreationBanner: {
        marginTop: 0,
        marginBottom: 0,
    },
    copyBlock: {
        gap: 10,
        maxWidth: 860,
    },
    title: {
        ...fonts.h1,
        color: colors.text900,
    },
    bodyList: {
        gap: 10,
        maxWidth: 760,
    },
    bodyItem: {
        alignItems: 'flex-start',
        flexDirection: 'row',
        gap: 12,
    },
    bodyBullet: {
        backgroundColor: colors.primaryGreenDark,
        borderRadius: 999,
        height: 8,
        marginTop: 9,
        width: 8,
    },
    bodyText: {
        ...fonts.bodyMedium,
        color: colors.text600,
        flex: 1,
    },
    actionRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
    },
});
